import {noop} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DropdownEmpty} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {AsyncLoader} from '../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {SearchHelpCenterResultModel} from '../../../models/api/searchHelpCenterModel';

/*
 * Props.
 */

interface HelpCenterSearchResultsProps {
  query: string;
  status?: AsyncStatusesEnum;
  results?: SearchHelpCenterResultModel;
}

/*
 * Style.
 */

const HelpCenterResultsDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledQueryDiv = styled.div`
  flex: 0 0 auto;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  line-height: 22px;
  margin-bottom: 20px;
  max-height: 110px;
  overflow: hidden;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
`;

const StyledPlaceholderDiv = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledArticleDiv = styled(ChromelessButton)`
  padding-bottom: 20px;
  line-height: 18px;
`;

const StyledArticleTitle = styled.div`
  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledArticleDescription = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  padding-top: 2px;
  max-height: 38px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const StyledIconSpan = styled.span`
  display: inline;
  white-space: nowrap;
`;

const StyledIconLink = styled(Icon)`
  display: inline-block;
  color: ${(p) => p.theme.interactiveText};
  margin-bottom: -3px;
  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledArticleLinkIcon = styled(Icon)`
  display: inline-block;
  color: transparent;
  margin-bottom: -3px;
  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

/*
 * Component.
 */

export const HelpCenterSearchResults: FC<HelpCenterSearchResultsProps> = (props) => (
  <HelpCenterResultsDiv>
    <StyledQueryDiv>
      <Trans id="help-center-search-results-query">Results for {`"${props.query}"`}</Trans>
    </StyledQueryDiv>
    {maybeRenderArticles(props.results, props.status)}
  </HelpCenterResultsDiv>
);

function maybeRenderArticles(results?: SearchHelpCenterResultModel, status?: AsyncStatusesEnum) {
  if (status && status !== AsyncStatusesEnum.LOADED) {
    return (
      <StyledPlaceholderDiv>
        <AsyncLoader status={status} onRetryClick={noop} />
      </StyledPlaceholderDiv>
    );
  }

  if (!results || !results.articles.length) {
    return (
      <StyledPlaceholderDiv>
        <DropdownEmpty />
      </StyledPlaceholderDiv>
    );
  }

  function openInNewTab(url: string) {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_P4_SEARCH_CLICK, {is_search_result: true, item: url});
    return () => openInBrowser(url);
  }

  return (
    <>
      {results.articles.map((article) => (
        <StyledArticleDiv onClick={openInNewTab(article.url)} key={article.title}>
          <StyledArticleTitle>
            {article.title}
            <StyledIconSpan>
              &nbsp;
              <StyledArticleLinkIcon name="externalLinkSmall" />
            </StyledIconSpan>
          </StyledArticleTitle>
          <StyledArticleDescription>{article.text}</StyledArticleDescription>
        </StyledArticleDiv>
      ))}
      <StyledArticleDiv onClick={openInNewTab(results.url)}>
        <StyledArticleTitle>
          <Trans id="help-center-search-results-morelink">+{results.count} more posts</Trans>{' '}
          <StyledIconLink name="externalLinkSmall" />
        </StyledArticleTitle>
      </StyledArticleDiv>
    </>
  );
}
